import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { __GLOBAL_SESSION_ID } from "@/client/globals";

import { calculateNextDate, determineNextDate } from "./utils";

export interface SentimentCheckSliceState {
  /** Assessment open date */
  openDate: string | null;
  /** Date of the next Sentiment Check */
  date: string | null;
  /** Whether the sentiment check date has passed */
  passed: boolean;
  /** Configured interval in hours for the sentiment checks in the assessment. */
  interval: number;
  /** Previous session ID when the last sentiment check was done. */
  prevSessionId: string | null;
}

export const initialState: SentimentCheckSliceState = {
  openDate: null,
  date: null,
  passed: false,
  interval: 24,
  prevSessionId: null,
};

/** Payload to start sentiment check listener in the Redux slice. */
interface SetSentimentCheckIntervalPayload {
  /** Sentiment check interval in hours. */
  interval: number;
  /** Assessment open date */
  openDate: string | null;
  /** Assessment end date */
  endDate: string | null;
  /** Session ID of the last sentiment check. */
  prevSessionId: string | null;
  /** Date of the last sentiment check. */
  lastCheckDate: string | null;
}

export const sentimentCheckSlice = createSlice({
  name: "sentimentCheck",
  initialState,
  reducers: {
    /** Init the sentiment checker state. */
    setSentimentCheckData(
      state,
      action: PayloadAction<SetSentimentCheckIntervalPayload>
    ) {
      const { interval, openDate, endDate, prevSessionId, lastCheckDate } =
        action.payload;

      state.interval = interval;
      state.openDate = openDate;
      state.prevSessionId = prevSessionId;

      const nextDate = calculateNextDate(openDate, interval, endDate);

      if (nextDate && openDate) {
        const nextDateInInterval = determineNextDate(
          lastCheckDate,
          nextDate,
          interval,
          openDate
        );
        state.date = nextDateInInterval;
      }
    },
    /** Set the date for the sentiment check. */
    nextSentimentCheck(state, action: PayloadAction<{ date: string }>) {
      state.date = action.payload.date;
      state.prevSessionId = __GLOBAL_SESSION_ID.current;
    },
    /** Mark the sentiment check date as passed. */
    endSentimentCheck(state) {
      state.passed = true;
    },
  },
});

export const { setSentimentCheckData, nextSentimentCheck, endSentimentCheck } =
  sentimentCheckSlice.actions;

export const sentimentCheckReducer = sentimentCheckSlice.reducer;
